import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, Clock, Filter } from 'lucide-react';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import { api } from '../lib/api';
import { CATEGORIES } from '../data/categories';
import { trackClinic } from '../lib/tracking';

export default function Klinikker() {
  const [all, setAll] = useState([]);
  const [city, setCity] = useState('');
  const [cat, setCat] = useState('');
  const [sort, setSort] = useState('featured');

  useEffect(() => {
    api.get('/clinics').then((r) => setAll(r.data.clinics || [])).catch(() => {});
  }, []);

  const cities = useMemo(() => [...new Set(all.map((c) => c.city))], [all]);

  const filtered = useMemo(() => {
    let list = all.filter((c) => !city || c.city === city).filter((c) => !cat || c.categories.includes(cat));
    list = [...list];
    if (sort === 'rating') list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    else if (sort === 'wait') list.sort((a, b) => (a.waitTime || '').localeCompare(b.waitTime || ''));
    else list.sort((a, b) => {
      if (a.placeholder && !b.placeholder) return 1;
      if (!a.placeholder && b.placeholder) return -1;
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return (b.rating || 0) - (a.rating || 0);
    });
    return list;
  }, [all, city, cat, sort]);

  return (
    <Layout>
      <Seo
        title="Private klinikker med 4+ stjerner | Specialists.no"
        description="Se alle godkjente partnerklinikker på Specialists.no. Kun klinikker med dokumentert 4+ stjerner."
        canonical="https://specialists.no/klinikker"
      />
      <section className="bg-[#0A1F44] text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B82F6] mb-3">KLINIKKOVERSIKT</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold">Norges best vurderte private klinikker</h1>
          <p className="text-white/70 mt-4 text-lg max-w-2xl">Kun klinikker med minst 4.0 stjerner og dokumentert medisinsk personell.</p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-[#0A1F44]/10 rounded-2xl p-5 mb-8 flex flex-wrap gap-4 items-center">
            <Filter size={18} className="text-[#0A1F44]" />
            <select data-testid="filter-city" value={city} onChange={(e) => setCity(e.target.value)} className="bg-[#FAF8F5] border-0 rounded-lg px-4 py-2 text-sm">
              <option value="">Alle byer</option>
              {cities.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
            <select data-testid="filter-category" value={cat} onChange={(e) => setCat(e.target.value)} className="bg-[#FAF8F5] border-0 rounded-lg px-4 py-2 text-sm">
              <option value="">Alle kategorier</option>
              {CATEGORIES.map((c) => (<option key={c.slug} value={c.name}>{c.name}</option>))}
            </select>
            <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[#FAF8F5] border-0 rounded-lg px-4 py-2 text-sm ml-auto">
              <option value="featured">Mest populær</option>
              <option value="rating">Høyest rating</option>
              <option value="wait">Raskest ventetid</option>
            </select>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c) => (
              <Link
                key={c.id}
                to={c.placeholder ? '#' : `/klinikker/${c.id}`}
                onClick={() => !c.placeholder && trackClinic(c.name)}
                data-testid={`clinic-${c.id}`}
                className={`bg-white rounded-2xl p-6 border border-[#0A1F44]/10 transition-all ${c.placeholder ? 'opacity-50 pointer-events-none' : 'hover:-translate-y-1 hover:shadow-lg hover:border-[#0A1F44]/30'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-1">
                    {!c.placeholder && <>
                      <Star size={14} fill="#3B82F6" className="text-[#3B82F6]" />
                      <span className="font-semibold text-[#0A1F44]">{c.rating}</span>
                      <span className="text-xs text-[#64748B]">({c.reviews})</span>
                    </>}
                  </div>
                  {c.featured && <span className="text-[10px] uppercase tracking-wider font-bold text-[#3B82F6] bg-[#3B82F6]/10 px-2 py-1 rounded-full">Partner</span>}
                </div>
                <h3 className="font-display text-xl font-bold text-[#0A1F44] mb-1">{c.name}</h3>
                <p className="text-sm text-[#64748B] mb-4">{c.location}</p>
                <div className="flex items-center gap-2 text-xs mb-4">
                  <Clock size={12} className="text-[#3B82F6]" />
                  <span className="font-medium text-[#0A1F44]">{c.waitTime}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {c.categories.map((k) => (<span key={k} className="text-[10px] uppercase tracking-wider bg-[#FAF8F5] text-[#0A1F44] px-2 py-1 rounded-full">{k}</span>))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
